import type { DragAnchor } from "./gesture.ts";

/**
 * Where a drag may end (P2.8-T14; ADR-121 decision 3.1): any element carrying `data-drop="<surface>:<index>"`. The bag's
 * slots carry `bag:<slot>` and the bar's slots will carry `bar:<slot>` (P2.8-T29), so a drop is resolved by what lies
 * under the pointer, with no list of targets to keep, and a surface added later needs only its attribute.
 */

/**
 * The attribute value a drop target carries.
 *
 * @param anchor The surface and the index.
 * @returns The value, `<surface>:<index>`.
 * @example
 * dropValue({ surface: "bag", index: 3 });
 * // "bag:3"
 */
export function dropValue(anchor: DragAnchor): string {
  return `${anchor.surface}:${anchor.index}`;
}

/**
 * Reads a drop target's attribute value.
 *
 * @param value The value, or null.
 * @returns The anchor, or null for a value that names none.
 * @example
 * parseDrop("bag:12");
 * // { surface: "bag", index: 12 }
 */
export function parseDrop(value: string | null | undefined): DragAnchor | null {
  const match = value === null || value === undefined ? null : /^([a-z]+):(\d+)$/.exec(value);
  return match === null ? null : { surface: match[1] as string, index: Number(match[2]) };
}

/**
 * The drop target an element is or lies inside.
 *
 * @param element The element, or null.
 * @returns Its anchor, or null when it is inside none.
 */
export function dropOf(element: Element | null): DragAnchor | null {
  return parseDrop(element?.closest("[data-drop]")?.getAttribute("data-drop"));
}

/**
 * The drop target under a point of the page, or null when the point is over none (the world, a window's frame).
 *
 * @param x Client x in CSS pixels.
 * @param y Client y in CSS pixels.
 * @param root The document to look in.
 * @returns The anchor.
 */
export function dropTargetAt(x: number, y: number, root: Pick<Document, "elementFromPoint"> = document): DragAnchor | null {
  return dropOf(root.elementFromPoint(x, y));
}

/**
 * Where a drag of a bag slot never drops its stack: the Inventory window, whose slots rearrange and whose frame puts
 * the slot back, the chat, the hotbar, whose slots place the item, and the tool belt, whose slots take a tool.
 */
export const KEEP_AREAS = "[data-bag='window'], [data-hud-slot='chat'], [data-hud-slot='abilities'], [data-hud-slot='tools']";

/**
 * Whether a drag of a bag slot released at a point drops its stack onto the ground (P2.8-T34, ADR-091's amendment for
 * P2.8-T34, point 8; the author, 2026-10-10: "dropping onto anywhere other than chat or the hotbar should count as
 * lost/dropped"): anywhere outside the Inventory window, the chat and the hotbar (`KEEP_AREAS`), the world, another
 * window and the HUD alike. The areas are read by their boxes, since a HUD panel may let the pointer through.
 *
 * @param x Client x in CSS pixels.
 * @param y Client y in CSS pixels.
 * @param root The document to look in.
 * @returns True where a release drops the stack.
 * @example
 * dropsWhenReleased(640, 360);
 * // true unless the Inventory, the chat or the hotbar covers the screen's middle
 */
export function dropsWhenReleased(x: number, y: number, root: Pick<Document, "querySelectorAll"> = document): boolean {
  for (const area of root.querySelectorAll(KEEP_AREAS)) {
    const box = area.getBoundingClientRect();
    if (x >= box.left && x < box.right && y >= box.top && y < box.bottom) {
      return false;
    }
  }
  return true;
}
